import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function SubscribersPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSubscribed, setFilterSubscribed] = useState('true');
  const [filterSource, setFilterSource] = useState('');

  const fetchSubscribers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filterSubscribed) params.append('subscribed', filterSubscribed);
      if (filterSource) params.append('source', filterSource);
      if (search) params.append('search', search);

      const res = await fetch(`${API}/api/subscribers/list?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await res.json();
      setSubscribers(data.subscribers || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
    }
    setLoading(false);
  }, [page, filterSubscribed, filterSource, search]);

  useEffect(() => { fetchSubscribers(); }, [fetchSubscribers]);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await fetch(`${API}/api/subscribers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      fetchSubscribers();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleToggleSubscription = async (sub) => {
    try {
      const action = sub.subscribed ? 'unsubscribe' : 'resubscribe';
      if (!window.confirm(sub.subscribed 
        ? 'Unsubscribe ' + sub.email + ' from the mailing list?' 
        : 'Re-subscribe ' + sub.email + ' to the mailing list?')) return;
      
      if (sub.subscribed && sub.unsubscribe_token) {
        await fetch(API + '/api/subscribers/unsubscribe?token=' + sub.unsubscribe_token);
      } else {
        await fetch(API + '/api/subscribers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('authToken') },
          body: JSON.stringify({ name: sub.name, email: sub.email, source: sub.source })
        });
      }
      fetchSubscribers();
    } catch (err) {
      console.error('Failed to toggle subscription:', err);
    }
  };

  const handleDownload = () => {
    const params = new URLSearchParams();
    if (filterSubscribed) params.append('subscribed', filterSubscribed);
    if (filterSource) params.append('source', filterSource);
    window.open(`${API}/api/subscribers/download?${params}`, '_blank');
  };

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Mailing List</h1>
          <p style={{ color: '#6B7280', fontSize: 14, marginTop: 4 }}>{total} subscriber{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={handleDownload} style={btnStyle}>
          <i className="fas fa-download" style={{ marginRight: 8 }}></i>Download CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search name or email..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={inputStyle}
        />
        <select value={filterSubscribed} onChange={e => { setFilterSubscribed(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All</option>
          <option value="true">Subscribed</option>
          <option value="false">Unsubscribed</option>
        </select>
        <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Sources</option>
          <option value="contact_form">Contact Form</option>
          <option value="order_form">Order Form</option>
          <option value="careers_form">Careers Form</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : subscribers.length === 0 ? (
              <tr><td colSpan="6" style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No subscribers found</td></tr>
            ) : (
              subscribers.map(sub => (
                <tr key={sub.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td style={tdStyle}>{sub.name}</td>
                  <td style={tdStyle}><a href={`mailto:${sub.email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>{sub.email}</a></td>
                  <td style={tdStyle}>
                    <span style={{
                      ...badgeBase,
                      background: sub.source === 'order_form' ? '#EEF2FF' : sub.source === 'careers_form' ? '#FDF2F8' : '#F0FDF4',
                      color: sub.source === 'order_form' ? '#4F46E5' : sub.source === 'careers_form' ? '#DB2777' : '#16A34A'
                    }}>
                      {sub.source.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button onClick={() => handleToggleSubscription(sub)} style={{
                      ...badgeBase,
                      background: sub.subscribed ? '#F0FDF4' : '#FEF2F2',
                      color: sub.subscribed ? '#16A34A' : '#DC2626',
                      border: 'none', cursor: 'pointer',
                    }} title={sub.subscribed ? 'Click to unsubscribe' : 'Click to re-subscribe'}>
                      {sub.subscribed ? '✓ Active' : '✕ Unsubscribed'}
                    </button>
                  </td>
                  <td style={tdStyle}>{new Date(sub.created_at).toLocaleDateString('en-GB')}</td>
                  <td style={tdStyle}>
                    <button onClick={() => handleDelete(sub.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14 }}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>← Prev</button>
          <span style={{ padding: '8px 16px', fontSize: 14, color: '#6B7280' }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>Next →</button>
        </div>
      )}
    </div>
  );
}

const btnStyle = { display: 'inline-flex', alignItems: 'center', background: 'linear-gradient(135deg,#10B981,#3B82F6)', color: 'white', padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 };
const inputStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, minWidth: 220, background: '#F9FAFB' };
const selectStyle = { padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, background: '#F9FAFB', cursor: 'pointer' };
const thStyle = { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' };
const tdStyle = { padding: '14px 16px', fontSize: 14 };
const badgeBase = { padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' };
const pageBtnStyle = { padding: '8px 16px', border: '1px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 };
